import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal/Modal';
import Btn from '../Btn/Btn';
import SpinnerBox from '../Spinner/SpinnerBox';
import DatePicker from '../DatePicker/DatePicker';
import { QuestionnaireSummary, PatientInfo } from './types';
import { QuestionnaireKind, isTherapyMaterialKind } from '../../types/QuestionnaireKind';
import './AssignQuestionnaireModal.css';

type Props = {
    questionnaire: QuestionnaireSummary | null;
    patients: PatientInfo[];
    isLoadingPatients: boolean;
    patientsError: string | null;
    isAssigning: boolean;
    assignError: string | null;
    downloadError: string | null;
    isDownloading: boolean;
    onClose: () => void;
    onAssign: (
        selectedPatientIds: Set<string>,
        startsOn: Date,
        endsOn?: Date,
        isRecurring?: boolean,
        recurrenceType?: 'daily' | 'weekly',
        recurrenceEndDate?: Date,
    ) => Promise<void>;
    onDownload: () => Promise<void>;
    onLoadPatients: () => Promise<void>;
    onDelete: () => void;
    onEdit: () => void;
    isPreparingEdit?: boolean;
    editError?: string | null;
    resourceKind: QuestionnaireKind;
};

const AssignQuestionnaireModal = ({
    questionnaire,
    patients,
    isLoadingPatients,
    patientsError,
    isAssigning,
    assignError,
    downloadError,
    isDownloading,
    onClose,
    onAssign,
    onDownload,
    onLoadPatients,
    onDelete,
    onEdit,
    isPreparingEdit = false,
    editError,
    resourceKind,
}: Props): JSX.Element | null => {
    const { t } = useTranslation();
    const isTherapyMaterial = isTherapyMaterialKind(resourceKind);
    const [patientSearchTerm, setPatientSearchTerm] = useState('');
    const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());
    const [startsOn, setStartsOn] = useState<Date>(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    });
    const [hasEndDate, setHasEndDate] = useState(false);
    const [endsOn, setEndsOn] = useState<Date>(() => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [isRecurringExpanded, setIsRecurringExpanded] = useState(false);
    const [recurrenceType, setRecurrenceType] = useState<'daily' | 'weekly' | null>(null);
    const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date>(() => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        date.setHours(0, 0, 0, 0);
        return date;
    });

    // isRecurring is true when a recurrence type is selected
    const isRecurring = recurrenceType !== null;

    useEffect(() => {
        if (questionnaire) {
            onLoadPatients();
            const newStart = new Date();
            newStart.setHours(0, 0, 0, 0);
            const newEnd = new Date(newStart);
            newEnd.setDate(newEnd.getDate() + 7);
            setStartsOn(newStart);
            setHasEndDate(false);
            setEndsOn(newEnd);
            setIsRecurringExpanded(false);
            setRecurrenceType(null);
            setRecurrenceEndDate(newEnd);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionnaire]);

    const handlePatientToggle = (patientId: string): void => {
        setSelectedPatients((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(patientId)) {
                newSet.delete(patientId);
            } else {
                newSet.add(patientId);
            }
            return newSet;
        });
    };

    const handleAssign = async (): Promise<void> => {
        await onAssign(
            selectedPatients,
            startsOn,
            hasEndDate ? endsOn : undefined,
            isRecurring,
            recurrenceType as 'daily' | 'weekly',
            recurrenceEndDate,
        );
    };

    const hasInvalidAvailabilityWindow = hasEndDate && endsOn <= startsOn;

    const filteredPatients = patients.filter((patient) => {
        if (!patient) {
            return false;
        }
        if (!patientSearchTerm.trim()) {
            return true;
        }
        const search = patientSearchTerm.toLowerCase();
        return (
            patient.name?.toLowerCase().includes(search) ||
            patient.username?.toLowerCase().includes(search) ||
            patient.id?.toLowerCase().includes(search)
        );
    });

    if (!questionnaire) {
        return null;
    }

    return (
        <Modal
            id="frontpage-assignment-modal"
            title={isTherapyMaterial ? t('Assign Therapy Material') : t('Assign questionnaire')}
            close={onClose}
        >
            <div className="frontpage__assign-form">
                <p>
                    {isTherapyMaterial
                        ? t('Select one or more patients to assign this therapy material to.')
                        : t('Select one or more patients to assign this questionnaire to.')}
                </p>
                <div className="frontpage__assign-details">
                    <div>
                        <strong>{isTherapyMaterial ? t('Therapy Material') : t('Questionnaire')}</strong>
                    </div>
                    <div>{questionnaire.displayName}</div>
                    <div className="frontpage__list-meta">
                        {questionnaire.isPrivate
                            ? t('Private: only visible to you.')
                            : t('Shared: changes and deletion apply to all therapists.')}
                    </div>
                </div>
                <fieldset className="frontpage__availability-section" disabled={isAssigning}>
                    <legend>{t('Availability')}</legend>
                    <div className="frontpage__availability-fields">
                        <label className="frontpage__availability-field">
                            <strong>
                                {isRecurring
                                    ? t('Recurring assignments start on the selected date')
                                    : t('Start date and time')}
                            </strong>
                            <DatePicker
                                type="datetime"
                                disabled={isAssigning}
                                selected={startsOn}
                                timeIntervals={15}
                                callback={(date: Date) => {
                                    const previousDuration = endsOn.getTime() - startsOn.getTime();
                                    setStartsOn(date);
                                    if (hasEndDate) {
                                        setEndsOn(new Date(date.getTime() + previousDuration));
                                    }
                                }}
                            />
                        </label>
                        <label className="frontpage__availability-toggle">
                            <input
                                type="checkbox"
                                checked={hasEndDate}
                                onChange={(event) => {
                                    const enabled = event.target.checked;
                                    setHasEndDate(enabled);
                                    if (enabled) {
                                        const defaultEnd = new Date(startsOn);
                                        defaultEnd.setDate(defaultEnd.getDate() + 7);
                                        setEndsOn(defaultEnd);
                                    }
                                }}
                            />
                            <span>{t('Add end date')}</span>
                        </label>
                        {hasEndDate && (
                            <label className="frontpage__availability-field">
                                <strong>{t('End date and time')}</strong>
                                <DatePicker
                                    type="datetime"
                                    disabled={isAssigning}
                                    selected={endsOn}
                                    timeIntervals={15}
                                    callback={setEndsOn}
                                />
                            </label>
                        )}
                    </div>
                    <p className="frontpage__availability-hint">
                        {hasEndDate
                            ? t('Patients can access this assignment during the selected availability window.')
                            : t('This assignment has no end date.')}
                    </p>
                    {hasInvalidAvailabilityWindow && (
                        <div className="frontpage__error">{t('End date must be after start date.')}</div>
                    )}
                </fieldset>
                <div className="frontpage__recurring-section">
                    <div
                        className="frontpage__recurring-header"
                        onClick={() => !isAssigning && setIsRecurringExpanded(!isRecurringExpanded)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (!isAssigning && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                setIsRecurringExpanded(!isRecurringExpanded);
                            }
                        }}
                    >
                        <div className="frontpage__recurring-checkbox">
                            <span>
                                {t('Repeat')}{' '}
                                {isRecurring && `(${recurrenceType === 'daily' ? t('Daily') : t('Weekly')})`}
                            </span>
                        </div>
                        <span
                            className={`frontpage__recurring-chevron ${
                                isRecurringExpanded ? 'frontpage__recurring-chevron--open' : ''
                            }`}
                        />
                    </div>
                    {isRecurringExpanded && (
                        <div className="frontpage__recurring-options">
                            <div className="frontpage__recurring-type">
                                <label>
                                    <input
                                        type="radio"
                                        name="recurrenceType"
                                        value="daily"
                                        checked={recurrenceType === 'daily'}
                                        onChange={(e) => {
                                            setRecurrenceType(e.target.value as 'daily' | 'weekly');
                                        }}
                                        onClick={(e) => {
                                            // Allow deselection by clicking the same radio button
                                            if (recurrenceType === 'daily') {
                                                setRecurrenceType(null);
                                                e.preventDefault();
                                            }
                                        }}
                                        disabled={isAssigning}
                                    />
                                    <span>{t('Daily')}</span>
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="recurrenceType"
                                        value="weekly"
                                        checked={recurrenceType === 'weekly'}
                                        onChange={(e) => {
                                            setRecurrenceType(e.target.value as 'daily' | 'weekly');
                                        }}
                                        onClick={(e) => {
                                            // Allow deselection by clicking the same radio button
                                            if (recurrenceType === 'weekly') {
                                                setRecurrenceType(null);
                                                e.preventDefault();
                                            }
                                        }}
                                        disabled={isAssigning}
                                    />
                                    <span>{t('Weekly')}</span>
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="recurrenceType"
                                        value=""
                                        checked={recurrenceType === null}
                                        onChange={() => setRecurrenceType(null)}
                                        disabled={isAssigning}
                                    />
                                    <span>{t('None (single assignment)')}</span>
                                </label>
                            </div>
                            {recurrenceType !== null && (
                                <div className="frontpage__recurring-end">
                                    <label>
                                        <strong>{t('Repeat until')}</strong>
                                    </label>
                                    <DatePicker
                                        type="date"
                                        disabled={isAssigning}
                                        selected={recurrenceEndDate}
                                        callback={(date: Date) => {
                                            const newDate = new Date(date);
                                            newDate.setHours(0, 0, 0, 0);
                                            setRecurrenceEndDate(newDate);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {isLoadingPatients ? (
                    <div className="frontpage__loading">
                        <SpinnerBox />
                        <p>{t('Loading patients...')}</p>
                    </div>
                ) : patientsError ? (
                    <div className="frontpage__error">{patientsError}</div>
                ) : (
                    <div className="frontpage__patient-list-container">
                        {patients.length > 0 && (
                            <input
                                type="text"
                                className="frontpage__search-input"
                                placeholder={t('Search patients...')}
                                value={patientSearchTerm}
                                onChange={(e) => setPatientSearchTerm(e.target.value)}
                            />
                        )}
                        {filteredPatients.length > 0 ? (
                            <ul className="frontpage__patient-list">
                                {filteredPatients
                                    .filter((patient) => patient != null)
                                    .map((patient) => (
                                        <li
                                            key={patient.id}
                                            className={`frontpage__patient-item ${
                                                selectedPatients.has(patient.id)
                                                    ? 'frontpage__patient-item--selected'
                                                    : ''
                                            }`}
                                            onClick={() => !isAssigning && handlePatientToggle(patient.id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                                if (!isAssigning && (e.key === 'Enter' || e.key === ' ')) {
                                                    e.preventDefault();
                                                    handlePatientToggle(patient.id);
                                                }
                                            }}
                                        >
                                            <div className="frontpage__patient-info">
                                                <span className="frontpage__patient-name">{patient.name}</span>
                                            </div>
                                        </li>
                                    ))}
                            </ul>
                        ) : patients.length > 0 ? (
                            <div className="frontpage__empty">{t('No patients found')}</div>
                        ) : (
                            <div className="frontpage__empty">{t('No patients available')}</div>
                        )}
                    </div>
                )}
                {assignError && <div className="frontpage__error">{assignError}</div>}
                {downloadError && <div className="frontpage__error">{downloadError}</div>}
                {editError && <div className="frontpage__error">{editError}</div>}
                <div className="frontpage__assign-actions">
                    <Btn
                        title={
                            isAssigning
                                ? t('Assigning...')
                                : selectedPatients.size > 0
                                ? t('Assign to {{count}} selected', { count: selectedPatients.size })
                                : t('Assign')
                        }
                        variant="primary"
                        onClick={handleAssign}
                        disabled={selectedPatients.size === 0 || isAssigning || hasInvalidAvailabilityWindow}
                    />
                    <Btn
                        title={isDownloading ? t('Downloading...') : t('Download')}
                        variant="secondary"
                        onClick={onDownload}
                        disabled={isDownloading}
                    />
                    <Btn
                        title={isPreparingEdit ? t('Opening...') : t('Edit')}
                        variant="secondary"
                        onClick={onEdit}
                        disabled={isPreparingEdit || isAssigning}
                    />
                    <Btn title={t('Cancel')} variant="secondary" onClick={onClose} />
                    <button type="button" className="regular-btn frontpage__delete-btn" onClick={onDelete}>
                        {isTherapyMaterial ? t('Delete Therapy Material') : t('Delete Questionnaire')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default AssignQuestionnaireModal;
